import { useState, useEffect } from "react";
import { Modal, Button, Space, Spin, Typography, Image } from "antd";
import {
  DownloadOutlined,
  ShareAltOutlined,
  LeftOutlined,
  RightOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { VaultFile } from "@/types";
import { FileIcon } from "@/components/shared";
import { PREVIEW_SUPPORTED_TYPES } from "@/utils/constants";
import { formatFileSize } from "@/utils/formatters";

const { Text, Title } = Typography;

interface PreviewModalProps {
  file: VaultFile | null;
  files?: VaultFile[];
  open: boolean;
  onClose: () => void;
  onDownload: (file: VaultFile) => void;
  onShare: (file: VaultFile) => void;
  onNavigate?: (file: VaultFile) => void;
  canShare?: boolean;
}

export default function PreviewModal({
  file,
  files = [],
  open,
  onClose,
  onDownload,
  onShare,
  onNavigate,
  canShare = true,
}: PreviewModalProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (file) {
      setLoading(true);
      setError(null);
      // Simulate loading the preview
      const timer = setTimeout(() => setLoading(false), 500);
      return () => clearTimeout(timer);
    }
  }, [file]);

  if (!file) return null;

  const currentIndex = files.findIndex((f) => f.id === file.id);
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < files.length - 1;

  const handlePrev = () => {
    if (hasPrev && onNavigate) {
      onNavigate(files[currentIndex - 1]);
    }
  };

  const handleNext = () => {
    if (hasNext && onNavigate) {
      onNavigate(files[currentIndex + 1]);
    }
  };

  const isPdf = PREVIEW_SUPPORTED_TYPES.pdf.includes(file.mimeType);
  const isImage = PREVIEW_SUPPORTED_TYPES.image.includes(file.mimeType);
  const isOffice = PREVIEW_SUPPORTED_TYPES.office.includes(file.mimeType);

  const renderPreview = () => {
    if (loading) {
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            minHeight: 400,
          }}
        >
          <Spin size="large" />
        </div>
      );
    }

    if (error) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            minHeight: 400,
          }}
        >
          <Text type="danger">{error}</Text>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={() => onDownload(file)}
            style={{ marginTop: 16 }}
          >
            Download instead
          </Button>
        </div>
      );
    }

    if (isImage) {
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            minHeight: 400,
            background: "#f5f5f5",
          }}
        >
          <Image
            src={file.blobUrl}
            alt={file.fileName}
            style={{ maxHeight: "70vh", objectFit: "contain" }}
            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgesARD3I+wAAACBjSFJNAAB6JgAAgIQAAPoAAACA6AAAdTAAAOpgAAA6mAAAF3CculE8AAAACXBIWXMAAAsTAAALEwEAmpwYAAABWWlUWHRYTUw6Y29tLmFkb2JlLnhtcAAAAAAAPHg6eG1wbWV0YSB4bWxuczp4PSJhZG9iZTpuczptZXRhLyIgeDp4bXB0az0iWE1QIENvcmUgNS40LjAiPgogICA8cmRmOlJERiB4bWxuczpyZGY9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkvMDIvMjItcmRmLXN5bnRheC1ucyMiPgogICAgICA8cmRmOkRlc2NyaXB0aW9uIHJkZjphYm91dD0iIgogICAgICAgICAgICB4bWxuczp0aWZmPSJodHRwOi8vbnMuYWRvYmUuY29tL3RpZmYvMS4wLyI+CiAgICAgICAgIDx0aWZmOk9yaWVudGF0aW9uPjE8L3RpZmY6T3JpZW50YXRpb24+CiAgICAgIDwvcmRmOkRlc2NyaXB0aW9uPgogICA8L3JkZjpSREY+CjwveDp4bXBtZXRhPgpMwidZAAAAm0lEQVRoBe3BMQEAAADCoPVP7WsIoAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAeA1v9QABT9ia6QAAAABJRU5ErkJggg=="
          />
        </div>
      );
    }

    if (isPdf) {
      return (
        <iframe
          src={`${file.blobUrl}#toolbar=0`}
          style={{
            width: "100%",
            height: "70vh",
            border: "none",
          }}
          title={file.fileName}
        />
      );
    }

    if (isOffice) {
      const encodedUrl = encodeURIComponent(file.blobUrl);
      return (
        <iframe
          src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodedUrl}`}
          style={{
            width: "100%",
            height: "70vh",
            border: "none",
          }}
          title={file.fileName}
        />
      );
    }

    // Fallback for unsupported types
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          minHeight: 400,
          padding: 48,
        }}
      >
        <FileIcon mimeType={file.mimeType} size="large" />
        <Title level={4} style={{ marginTop: 24, marginBottom: 8 }}>
          {file.fileName}
        </Title>
        <Text type="secondary" style={{ marginBottom: 24 }}>
          {formatFileSize(file.size)} · Preview not available for this file type
        </Text>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={() => onDownload(file)}
        >
          Download file
        </Button>
      </div>
    );
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width="90vw"
      style={{ top: 20 }}
      styles={{
        body: { padding: 0 },
      }}
      closeIcon={<CloseOutlined style={{ color: "#fff" }} />}
      title={
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            paddingRight: 40,
          }}
        >
          <Text strong ellipsis style={{ maxWidth: 400 }}>
            {file.fileName}
          </Text>
          <Space>
            <Button
              icon={<DownloadOutlined />}
              onClick={() => onDownload(file)}
            >
              Download
            </Button>
            {canShare && (
              <Button icon={<ShareAltOutlined />} onClick={() => onShare(file)}>
                Share
              </Button>
            )}
          </Space>
        </div>
      }
    >
      <div style={{ position: "relative" }}>
        {renderPreview()}

        {/* Navigation arrows */}
        {files.length > 1 && (
          <>
            {hasPrev && (
              <Button
                type="text"
                icon={<LeftOutlined />}
                onClick={handlePrev}
                style={{
                  position: "absolute",
                  left: 16,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "rgba(0,0,0,0.5)",
                  color: "#fff",
                  border: "none",
                }}
              />
            )}
            {hasNext && (
              <Button
                type="text"
                icon={<RightOutlined />}
                onClick={handleNext}
                style={{
                  position: "absolute",
                  right: 16,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "rgba(0,0,0,0.5)",
                  color: "#fff",
                  border: "none",
                }}
              />
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
